import { RegisterUser } from '@/apis/users/requests.ts'
import { AlertOptions, useAppAlert } from '@/app/AppAlertProvider.tsx'
import LoadingCard from '@/app/common/components/LoadingCard'
import { EyeIcon, EyeSlashIcon } from '@/app/common/icons/general'
import { cn } from '@/app/common/utilities'
import { Button, buttonVariants } from '@/common/components/Button.tsx'
import { CircleLoader } from '@/common/components/CircleLoader.tsx'
import FormErrorMessage from '@/common/components/FormErrorMessage.tsx'
import { Input } from '@/common/components/Input.tsx'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import { ChangeEvent, useState } from 'react'
import { SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { PublicProgramQueries } from '../queries'
import { Link } from 'react-router-dom'
import TermsPDF from '@assets/ACE_Terms_of_Use.pdf'
import TermsPDFes from '@assets/Términos_de_Uso_de_las_Becas_ACE.pdf'
import { global_error_codes } from '@/common/constants.ts'

const UserRegistrationSchema = z
  .object({
    email: z.string().email({ message: 'please_enter_a_valid_email_address' }),
    password: z
      .string()
      .min(8, { message: 'password_must_be_at_least_characters_long' })
      .regex(/^(?=.*[A-Z])(?=.*[a-z])(?=.*[-!@#$%^&*(),.?":{}|<>]).{8,}$/, {
        message: 'password_must_contain_at_least_one_uppercase_letter',
      }),
    confirm_password: z.string().min(8, {
      message: 'confirm_password_must_be_at_least_characters_long',
    }),
    terms_and_conditions: z.literal(true, {
      errorMap: () => ({ message: 'you_must_accept_the_terms_and_conditions' }),
    }),
    user_has_accepted_text_notifications: z.boolean(),
    is_new_user: z.literal(true, {
      errorMap: () => ({ message: 'you_must_be_a_new_user' }),
    }),
    phone: z.string().min(8, 'Please enter a valid phone number'),
  })
  .refine((data) => data.password === data.confirm_password, {
    message: 'passwords_do_not_match',
    path: ['confirm_password'],
  })
type UserRegistrationForm = z.infer<typeof UserRegistrationSchema>
type UserRegistrationViewProps = {
  renderNextView: (registeredEmail: string) => void
  programID: string
}
export function UserRegistrationView({
  renderNextView,
  programID,
}: UserRegistrationViewProps) {
  const { t: translate, i18n } = useTranslation()
  const [requestInFlight, setRequestInFlight] = useState(false)
  const { fireAlert } = useAppAlert()
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false)
  const { status, data: programs } = useQuery(PublicProgramQueries.index())


  const termsLink = i18n.language === 'es' ? TermsPDFes : TermsPDF

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    control,
  } = useForm<UserRegistrationForm>({
    resolver: zodResolver(UserRegistrationSchema),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  })

  const onSubmit: SubmitHandler<UserRegistrationForm> = async (formData) => {
    if (programID == null) return
    setRequestInFlight(true)
    const { error } = await RegisterUser({
      email: formData.email,
      password: formData.password,
      program_id: parseInt(programID),
      user_has_accepted_text_notifications:
        formData.user_has_accepted_text_notifications ?? false,
      phone: formData.phone,
    })
    setRequestInFlight(false)
    if (error) {
      const alertOptions: AlertOptions = {
        variant: 'error',
      }
      if (error.code == global_error_codes.v1_registration_guard) {
        const defaultMessage = error.message.replace('{v1_access_url}', '')
        const splitMessage = error.message.split('{v1_access_url}')

        if (splitMessage.length == 2) {
          const url =
            typeof error.details?.url != 'string' ? '' : error.details?.url
          alertOptions.messageComponent = (
            <span>
              {splitMessage[0]}
              <a
                className="underline text-secondary"
                target="_blank"
                href={url}
              >
                {url}
              </a>
              {splitMessage[1]}
            </span>
          )
        } else {
          alertOptions.message = defaultMessage
        }
      } else {
        alertOptions.message = error.message
      }
      await fireAlert(alertOptions)
      return
    }
    renderNextView(formData.email)
  }

  const password = useWatch({ control, name: 'password' })
  const confirmPassword = useWatch({ control, name: 'confirm_password' })

  const formatPhone = (phoneNumber: string) => {
    let value = phoneNumber.replace(/\D/g, '')
    if (value.length > 10) value = value.slice(0, 10)

    const formattedValue = value.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')
    return formattedValue
  }

  const handlePhoneChange = (e: ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatPhone(e.target.value)
    setValue('phone', formattedValue)
  }

  if (status === 'pending') {
    return <LoadingCard isLoading={true}>{translate('loading')}</LoadingCard>
  }

  if (status === 'error') {
    return <LoadingCard isLoading={true}>Error</LoadingCard>
  }

  const selectedProgram = programs.data.find(
    (program) => program.id === parseInt(programID)
  )

  if (!selectedProgram) {
    return null
  }

  return (
    <form noValidate className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
      <div>
        <label
          htmlFor="email"
          className="block text-sm font-medium leading-6 text-gray-900"
        >
          {translate('email')}
        </label>
        <Input
          id="email"
          {...register('email')}
          type="text"
          required
          autoComplete="home email"
          placeholder="example@gmail.com"
        />
        <FormErrorMessage message={translate(errors.email?.message ?? '')} />
      </div>

      <div>
        <label
          htmlFor="password"
          className="block text-sm font-medium leading-6 text-gray-900"
        >
          {translate('password')}
        </label>
        <div className="relative flex">
          <Input
            id="password"
            {...register('password')}
            type={showNewPassword ? 'text' : 'password'}
            required
            placeholder={translate('enter_password')}
            />
          <div
            className="absolute right-0 flex items-center h-full mr-2"
            onClick={() => {
              setShowNewPassword(!showNewPassword)
            }}
          >
            {showNewPassword ? <EyeIcon /> : <EyeSlashIcon />}
          </div>
        </div>
        <FormErrorMessage message={translate(errors.password?.message ?? '')} />
      </div>

      <div>
        <label
          htmlFor="confirm_password"
          className="block text-sm font-medium leading-6 text-gray-900"
        >
          {translate('confirm_password')}
        </label>
        <div className="relative flex">
          <Input
            id="confirm_password"
            {...register('confirm_password')}
            type={showConfirmNewPassword ? 'text' : 'password'}
            required
            placeholder={translate("confirm_password")}
            />
          <div
            className="absolute right-0 flex items-center h-full mr-2"
            onClick={() => {
              setShowConfirmNewPassword(!showConfirmNewPassword)
            }}
          >
            {showConfirmNewPassword ? <EyeIcon /> : <EyeSlashIcon />}
          </div>
        </div>
        <FormErrorMessage
          message={translate(
            confirmPassword && password !== confirmPassword
              ? 'passwords_do_not_match'
              : errors.confirm_password?.message
                ? errors.confirm_password.message
                : ''
          )}
        />
      </div>
      <div>
        <label
          htmlFor="phone"
          className="block text-sm font-medium leading-6 text-gray-900"
        >
          {translate('phone')}
        </label>
        <div className="relative flex">
          <Input
            {...register('phone')}
            maxLength={10}
            id="phone"
            name="phone"
            type="tel"
            autoComplete="tel"
            placeholder="(123) 456-7890"
            onChange={handlePhoneChange}
          />{' '}
        </div>
        <FormErrorMessage message={translate(errors.phone?.message ?? '')} />
      </div>
      <div className="flex gap-x-3">
        <Input
          {...register('terms_and_conditions')}
          id="terms_accepted"
          type="checkbox"
          className="mt-1 h-5 w-5 text-primary-600 focus:ring-primary-600"
        />
        <label
          htmlFor="terms_accepted"
          className="block text-sm font-normal leading-6 text-gray-600"
        >
          {translate('registration_page.terms_and_conditions_part_one', {program_name: selectedProgram.name})}
          {' '}
          <Link
            target="_blank"
            to={termsLink}
            className={buttonVariants({ variant: 'text', size: 'text' })}
          >
            {translate('terms_and_conditions')}
          </Link>.
        </label>
      </div>
      <FormErrorMessage
        message={translate(
          errors.terms_and_conditions?.message ?? ''
        )}
      />
      <div className="flex gap-x-3">
        <Input
          {...register('user_has_accepted_text_notifications')}
          id="text_notifications"
          type="checkbox"
          className="mt-1 h-5 w-5 text-primary-600 focus:ring-primary-600"
        />
        <label
          htmlFor="text_notifications"
          className="block text-sm font-normal leading-6 text-gray-600"
        >
          {translate('registration_page.text_message_agreement', {program_name: selectedProgram.name})}{' '}
        </label>
      </div>
      <div>
        <div className="flex items-center gap-x-3">
          <Input
            {...register('is_new_user')}
            id="new_user"
            type="checkbox"
            className="mt-1 h-5 w-5 text-primary-600 focus:ring-primary-600"
          />
          <label
            htmlFor="new_user"
            className="block text-md font-medium leading-6 text-gray-900"
          >
            {translate('i_am_a_new_program_applicant', {program_name: selectedProgram.name ?? 'Program'})}
          </label>
        </div>
        <p className="text-sm ml-8 text-gray-600">
          {translate('existing_applicants_register_here')}{' '}
          <a
            href="https://app.acescholarships.org"
            className={buttonVariants({ variant: 'text', size: 'text' })}
          >
            {format(selectedProgram.program_start_date, 'yyyy')}
            {'-'}
            {format(selectedProgram.program_end_date, 'yyyy')}{' '}
            {translate('here')}{' '}
          </a>
        </p>
        <FormErrorMessage
          message={translate(errors.is_new_user?.message ?? '')}
        />
      </div>

      <div className="flex">
        <Button
          type="submit"
          className={cn('grow space-x-2')}
          disabled={requestInFlight}
        >
          {requestInFlight && <CircleLoader size="sm" />}
          <span>
            {requestInFlight
              ? `${translate('registering')}...`
              : translate('register')}
          </span>
        </Button>
      </div>
    </form>
  )
}
